"use client"

import { useState, type ReactNode } from "react"
import { ArrowRightLeft, CircleAlert, CircleCheck, ShieldCheck, type LucideIcon } from "lucide-react"
import { Area, AreaChart, ResponsiveContainer, Tooltip as RechartsTooltip } from "recharts"

import { KpiCard, KpiCardSkeleton } from "@/components/ui/analytics/kpi-card"
import { Button } from "@/components/ui/button"
import { OutcomeCorrectionDialog } from "@/components/ui/conversation/outcome-correction-dialog"
import { Skeleton } from "@/components/ui/skeleton"
import { usePermissions } from "@/hooks/use-permissions"
import type { MetricKind } from "@/lib/analytics/deltas"
import { formatShare } from "@/lib/analytics/format"
import { MESSAGING_AGENTS_PERMISSIONS } from "@/lib/auth/permissions.constants"
import {
  ATTRIBUTION_CAPTIONS,
  COHORT_DEFINITIONS,
  COHORT_LABELS,
  handedToTeamCounts,
  NO_VALUE,
  OUTCOME_METRIC_DEFINITIONS,
  requestCount,
  resolutionCounts,
  resolutionRate,
  selfServiceCounts,
  selfServiceShare,
  SERVICE_METRIC_DEFINITIONS,
  shareOf,
  totalOutcomes,
  type ShareCounts,
} from "@/lib/conversation-quality/metric-definitions"
import {
  COHORT_CHART_COLORS,
  ESCALATION_CATEGORY_LABELS,
  FADED_RESOLVED,
  HUMAN_OUTCOME_CHART_COLORS,
  HUMAN_OUTCOME_DEFINITIONS,
  HUMAN_OUTCOME_LABELS,
  OUTCOME_CHART_COLORS,
  OUTCOME_DEFINITIONS,
  OUTCOME_LABELS,
  TAKEOVER_REASON_LABELS,
} from "@/lib/conversation-quality/outcome-display"
import {
  COHORTS,
  REQUEST_OUTCOMES,
  type CohortOutcomeCounts,
  type OutcomeCounts,
} from "@/lib/conversation-quality/outcomes"
import type { ThreadQuality } from "@/lib/conversation/thread-header"
import type { ConversationOutcomesResult } from "@/server-actions/conversation-quality"

import {
  ConversationNameLink,
  CountedApart,
  DefinedTerm,
  EmptyNote,
  formatWindow,
  InfoTip,
  RankedBars,
  requestsOf,
  requestTotal,
  SectionCard,
  SegmentLegend,
  sessionCount,
  shareComparison,
  StackedBar,
  type Segment,
} from "./section-parts"

export function SelfServiceTile({ data }: { data: ConversationOutcomesResult | undefined }) {
  if (!data) return <KpiCardSkeleton />

  const trend = data.weeklyTrend.map((week) => ({
    label: week.week,
    value: selfServiceShare(week.counts),
  }))

  return (
    <OutcomeShareTile
      data={data}
      counts={selfServiceCounts}
      kind="positive"
      icon={ShieldCheck}
      label={OUTCOME_METRIC_DEFINITIONS.selfServiceShare.label}
      definition={OUTCOME_METRIC_DEFINITIONS.selfServiceShare.definition}
      sparkline={
        trend.filter((point) => point.value !== null).length > 1 ? (
          <ShareSparkline points={trend} />
        ) : undefined
      }
    />
  )
}

export function HandedToTeamTile({ data }: { data: ConversationOutcomesResult | undefined }) {
  if (!data) return <KpiCardSkeleton />

  return (
    <OutcomeShareTile
      data={data}
      counts={handedToTeamCounts}
      kind="negative"
      icon={ArrowRightLeft}
      label={OUTCOME_LABELS.escalated}
      definition={OUTCOME_DEFINITIONS.escalated}
    />
  )
}

/** A share of the period's real requests — the ones «How sessions ended» splits — against the window before. */
function OutcomeShareTile({
  data,
  counts,
  kind,
  icon,
  label,
  definition,
  sparkline,
}: {
  data: ConversationOutcomesResult;
  counts: (split: CohortOutcomeCounts) => ShareCounts;
  kind: MetricKind;
  icon: LucideIcon;
  label: string;
  definition: string;
  sparkline?: ReactNode;
}) {
  const now = counts(data.current)
  const current = shareOf(now)

  return (
    <KpiCard
      label={label}
      tooltip={definition}
      icon={icon}
      formattedValue={formatShare(current, NO_VALUE)}
      {...shareComparison(current, data.hasComparison ? counts(data.previous) : null, kind)}
      subtitle={requestsOf(now)}
      sparkline={sparkline}
    />
  )
}

/** Eight weeks whatever the period: a share needs weeks to show a direction. */
function ShareSparkline({ points }: { points: { label: string; value: number | null }[] }) {
  return (
    <ResponsiveContainer width="100%" height={40}>
      <AreaChart data={points} margin={{ top: 2, right: 2, bottom: 2, left: 2 }}>
        <defs>
          <linearGradient id="sparkSelfService" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={OUTCOME_CHART_COLORS.resolved_positive} stopOpacity={0.3} />
            <stop offset="100%" stopColor={OUTCOME_CHART_COLORS.resolved_positive} stopOpacity={0} />
          </linearGradient>
        </defs>
        <RechartsTooltip
          position={{ y: 44 }}
          content={({ active, payload }) => {
            if (!active || !payload?.length) return null
            const point = payload[0].payload as { label: string; value: number | null }
            return (
              <div className="rounded border bg-background px-2 py-1 text-xs shadow-sm">
                <div className="text-muted-foreground">{`Week of ${point.label}`}</div>
                <div className="font-medium">{formatShare(point.value, NO_VALUE)}</div>
              </div>
            )
          }}
        />
        <Area
          type="monotone"
          dataKey="value"
          stroke={OUTCOME_CHART_COLORS.resolved_positive}
          fill="url(#sparkSelfService)"
          strokeWidth={1.5}
          dot={false}
          connectNulls
          isAnimationActive={false}
        />
      </AreaChart>
    </ResponsiveContainer>
  )
}

/** The funnel beside who handled it, and the review queue's state in one line beneath. */
export function OutcomesCard({
  data,
  timezone,
}: {
  data: ConversationOutcomesResult | undefined;
  timezone: string;
}) {
  const totals = data ? totalOutcomes(data.current) : undefined
  const segments = totals ? outcomeSegments(totals) : []
  const total = totals ? requestCount(totals) : 0
  const noise = totals?.spam ?? 0
  const teamStarted = data?.teamStarted ?? 0

  return (
    <SectionCard
      title={OUTCOME_METRIC_DEFINITIONS.outcomes.label}
      definition={OUTCOME_METRIC_DEFINITIONS.outcomes.definition}
      subtitle={data && `${requestTotal(total)} · ${formatWindow(data.range, timezone)}`}
    >
      {!data ? (
        <Skeleton className="h-40 w-full" />
      ) : (
        <div className="grid gap-6 md:grid-cols-[5fr_6fr] md:gap-10">
          <div className="space-y-4">
            {total === 0 ? (
              <EmptyNote text="No session in this period has been judged yet." />
            ) : (
              <>
                <StackedBar segments={segments} total={total} className="h-3" />
                <SegmentLegend segments={segments} total={total} />
              </>
            )}
            <CountedApart
              count={noise}
              one="1 session was not a request and is left out of every rate."
              many="sessions were not a request and are left out of every rate."
            />
            <CountedApart
              count={teamStarted}
              one="1 thread your team started itself is counted apart and left out of every rate."
              many="threads your team started itself are counted apart and left out of every rate."
            />
          </div>
          <div className="space-y-4">
            <CohortBars data={data} />
            <ReviewQueueLine rows={data.needsReview} />
          </div>
        </div>
      )}
    </SectionCard>
  )
}

function CohortBars({ data }: { data: ConversationOutcomesResult }) {
  const segments = cohortSegments(data.current)
  const total = segments.reduce((sum, segment) => sum + segment.count, 0)

  return (
    <div className="space-y-4">
      <div className="space-y-0.5">
        <div className="flex items-center gap-2 text-sm font-medium">
          <span>{OUTCOME_METRIC_DEFINITIONS.byCohort.label}</span>
          <InfoTip
            label={OUTCOME_METRIC_DEFINITIONS.byCohort.label}
            definition={OUTCOME_METRIC_DEFINITIONS.byCohort.definition}
          />
        </div>
        <p className="text-xs text-muted-foreground">{ATTRIBUTION_CAPTIONS.cohort}</p>
      </div>
      {/* Empty only when the funnel beside it is, and that one already says so. */}
      {total > 0 && (
        <>
          <StackedBar segments={segments} total={total} className="h-3" />
          <SegmentLegend segments={segments} total={total} />
        </>
      )}
      <ResolutionLine split={data.current} />
    </div>
  )
}

/** Every request by who carried it — the question the block is named for, and one the funnel above cannot answer. */
function cohortSegments(split: CohortOutcomeCounts): Segment[] {
  return COHORTS.map((cohort) => ({
    key: cohort,
    label: COHORT_LABELS[cohort],
    count: requestCount(split[cohort]),
    color: COHORT_CHART_COLORS[cohort],
    definition: COHORT_DEFINITIONS[cohort],
  }))
}

/** How the agent did with what was left to it alone, so a week the team took more over does not pull it down. */
function ResolutionLine({ split }: { split: CohortOutcomeCounts }) {
  const counts = resolutionCounts(split)
  if (counts.over === 0) return null

  const { label, definition } = OUTCOME_METRIC_DEFINITIONS.resolutionRate
  return (
    <p className="text-sm">
      <DefinedTerm label={label} definition={definition} />
      <span>{`: ${requestTotal(counts.over)} · ${formatShare(resolutionRate(split), NO_VALUE)} resolved`}</span>
    </p>
  )
}

/** Withheld from someone who may not read conversations, like the queue itself. */
function ReviewQueueLine({ rows }: { rows: ConversationOutcomesResult["needsReview"] }) {
  if (rows === null) return null

  return (
    <div className="flex items-center gap-2 border-t pt-4 text-sm">
      {rows.length === 0 ? (
        <CircleCheck className="h-4 w-4 shrink-0 text-green-600" aria-hidden="true" />
      ) : (
        <CircleAlert className="h-4 w-4 shrink-0 text-amber-600" aria-hidden="true" />
      )}
      <span>{OUTCOME_METRIC_DEFINITIONS.needsReview.label}</span>
      <span className="text-xs text-muted-foreground">
        {rows.length === 0
          ? "· nothing is waiting for a person"
          : `· ${rows.length === 1 ? "1 verdict waits" : `${rows.length} verdicts wait`} for a person, below`}
      </span>
    </div>
  )
}

/** The three answers about a handover, side by side: why, what came of it, and who stepped in unasked. */
export function HandoverReasonsCard({ data }: { data: ConversationOutcomesResult | undefined }) {
  const rows = data?.escalationCategories.map((row) => ({
    key: row.category,
    label: ESCALATION_CATEGORY_LABELS[row.category],
    count: row.sessions,
  }))
  const judged = rows?.reduce((sum, row) => sum + row.count, 0) ?? 0

  return (
    <SectionCard
      title={OUTCOME_METRIC_DEFINITIONS.escalationCategories.label}
      definition={OUTCOME_METRIC_DEFINITIONS.escalationCategories.definition}
      subtitle={rows && `${sessionCount(judged)} judged as handed to team`}
    >
      {!rows ? (
        <Skeleton className="h-24 w-full" />
      ) : rows.length === 0 ? (
        <EmptyNote text="None in this period." />
      ) : (
        <RankedBars rows={rows} />
      )}
    </SectionCard>
  )
}

const REDIRECTED_LABEL = "Sent to email or phone"
const ANSWERED_BY_EMAIL_LABEL = "Answered by email"

export function HandoverOutcomesCard({ data }: { data: ConversationOutcomesResult | undefined }) {
  const segments = data?.handoverOutcomes.map(handoverSegment) ?? []
  const total = segments.reduce((sum, segment) => sum + segment.count, 0)

  return (
    <SectionCard
      title={OUTCOME_METRIC_DEFINITIONS.handoverOutcomes.label}
      subtitle={OUTCOME_METRIC_DEFINITIONS.handoverOutcomes.definition}
    >
      {!data ? (
        <Skeleton className="h-24 w-full" />
      ) : total === 0 ? (
        <EmptyNote text="No session in this period ended with your team." />
      ) : (
        <div className="space-y-4">
          <StackedBar segments={segments} total={total} className="h-3" />
          <SegmentLegend segments={segments} total={total} />
          {segments.some((segment) => segment.key === "answered_by_email") && (
            <p className="text-xs text-muted-foreground">
              <span className="font-medium">{ANSWERED_BY_EMAIL_LABEL}.</span>{" "}
              {SERVICE_METRIC_DEFINITIONS.answeredByEmail.definition}
            </p>
          )}
          {segments.some((segment) => segment.key === "redirected") && (
            <p className="text-xs text-muted-foreground">
              {REDIRECTED_LABEL}: the agent sent the customer to your team by email or phone, or promised
              your team would get in touch that way, so nobody was asked to reply here.
            </p>
          )}
        </div>
      )}
    </SectionCard>
  )
}

function handoverSegment({
  outcome,
  sessions,
}: ConversationOutcomesResult["handoverOutcomes"][number]): Segment {
  switch (outcome) {
    case null:
      return {
        key: "unjudged",
        label: "Not judged",
        count: sessions,
        color: "hsl(var(--muted-foreground))",
        definition: "The review could not tell how your team's part ended.",
      }
    case "answered_by_email":
      // The resolved colour, faded: the team answered, and nothing judged how that went.
      return { key: outcome, label: ANSWERED_BY_EMAIL_LABEL, count: sessions, color: FADED_RESOLVED }
    case "redirected":
      return { key: outcome, label: REDIRECTED_LABEL, count: sessions, color: OUTCOME_CHART_COLORS.escalated }
    default:
      return {
        key: outcome,
        label: HUMAN_OUTCOME_LABELS[outcome],
        count: sessions,
        color: HUMAN_OUTCOME_CHART_COLORS[outcome],
        definition: HUMAN_OUTCOME_DEFINITIONS[outcome],
      }
  }
}

export function TakeoverReasonsCard({
  data,
  takeovers,
}: {
  data: ConversationOutcomesResult | undefined;
  /** From the service metrics: every takeover, judged or not. */
  takeovers: number | undefined;
}) {
  const rows = data?.takeoverReasons.map((row) => ({
    key: row.reason,
    label: TAKEOVER_REASON_LABELS[row.reason],
    count: row.sessions,
  }))
  const judged = rows?.reduce((sum, row) => sum + row.count, 0) ?? 0

  return (
    <SectionCard
      title={OUTCOME_METRIC_DEFINITIONS.takeoverReasons.label}
      subtitle={OUTCOME_METRIC_DEFINITIONS.takeoverReasons.definition}
    >
      {!rows ? (
        <Skeleton className="h-24 w-full" />
      ) : (
        <div className="space-y-3">
          {rows.length === 0 ? <EmptyNote text="None in this period." /> : <RankedBars rows={rows} />}
          {takeovers !== undefined && takeovers > judged && (
            <p className="text-xs text-muted-foreground">
              {`${takeovers.toLocaleString()} takeovers · ${judged.toLocaleString()} judged so far`}
            </p>
          )}
        </div>
      )}
    </SectionCard>
  )
}

/** A card only while something waits; the outcomes card says so in a line otherwise. */
export function ReviewQueue({ data }: { data: ConversationOutcomesResult | undefined }) {
  const { can } = usePermissions()
  // Corrected where it is listed: the inbox badge always shows a conversation's newest verdict,
  // so an older session flagged here could never be reached from there.
  const [reviewing, setReviewing] = useState<ThreadQuality | null>(null)
  const canCorrect = can(MESSAGING_AGENTS_PERMISSIONS.MANAGE)
  const rows = data?.needsReview

  if (!rows || rows.length === 0) return null

  return (
    <SectionCard
      title={OUTCOME_METRIC_DEFINITIONS.needsReview.label}
      definition={OUTCOME_METRIC_DEFINITIONS.needsReview.definition}
      subtitle="newest first, whatever the period"
    >
      <ul className="-my-3 divide-y">
        {rows.map((row) => (
          <li
            key={row.quality.sessionId}
            className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between"
          >
            <div className="min-w-0">
              <ConversationNameLink href={row.href} name={row.contactTitle} />
              <p className="text-xs text-muted-foreground">{queuedVerdict(row)}</p>
              {row.quality.summary && (
                <p className="line-clamp-2 text-xs text-muted-foreground">{row.quality.summary}</p>
              )}
            </div>
            {canCorrect && (
              <Button variant="outline" size="sm" className="sm:shrink-0" onClick={() => setReviewing(row.quality)}>
                Review
              </Button>
            )}
          </li>
        ))}
      </ul>
      {reviewing && (
        <OutcomeCorrectionDialog
          quality={reviewing}
          open
          onOpenChange={(open) => {
            if (!open) setReviewing(null)
          }}
        />
      )}
    </SectionCard>
  )
}

function queuedVerdict({ quality, confidence }: NonNullable<ConversationOutcomesResult["needsReview"]>[number]): string {
  const verdict = `Session #${quality.sessionNumber} · ${OUTCOME_LABELS[quality.outcome]}`
  if (quality.outcomeSource === "manual") return `${verdict} · corrected, needs confirming`
  return confidence === null ? verdict : `${verdict} · ${formatShare(confidence, NO_VALUE)} sure`
}

function outcomeSegments(counts: OutcomeCounts): Segment[] {
  return REQUEST_OUTCOMES.map((outcome) => ({
    key: outcome,
    label: OUTCOME_LABELS[outcome],
    count: counts[outcome],
    color: OUTCOME_CHART_COLORS[outcome],
    definition: OUTCOME_DEFINITIONS[outcome],
  }))
}
